"use client";

import { c as _c } from "react/compiler-runtime";
import { useFormik } from "formik";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { isValidPhone, normalizePhoneForSubmit } from "@/shared/auth/phone";
import { Button } from "@/shared/ui/button";
import { Checkbox } from "@/shared/ui/checkbox";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/shared/ui/field";
import { PhoneInput } from "@/shared/ui/phone-input";
import { Spinner } from "@/shared/ui/spinner";
type RequestStepProps = {
  onSubmitted: (phone: string) => void;
};
const MOCK_DELAY_MS = 800;
const DELETED_ITEM_KEYS = [1, 2, 3, 4] as const;
export function RequestStep(t0) {
  const $ = _c(87);
  const {
    onSubmitted
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = yup.object({
      phone: yup.string().required(t("landing.accountDeletion.invalidPhone")).test("phone-format", t("landing.accountDeletion.invalidPhone"), _temp),
      confirm: yup.boolean().oneOf([true], t("landing.accountDeletion.confirmRequired"))
    });
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const schema = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      phone: "",
      confirm: false
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onSubmitted) {
    t3 = async values => {
      await new Promise(_temp2);
      onSubmitted(normalizePhoneForSubmit(values.phone));
    };
    $[3] = onSubmitted;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== schema || $[6] !== t3) {
    t4 = {
      initialValues: t2,
      validationSchema: schema,
      onSubmit: t3
    };
    $[5] = schema;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const form = useFormik(t4);
  let t5;
  if ($[8] !== t) {
    t5 = t("landing.accountDeletion.whatDeletedTitle");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <h2 className="text-base font-semibold text-slate-900">{t5}</h2>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t) {
    t7 = DELETED_ITEM_KEYS.map(key => <li key={key} className="flex items-start gap-2 text-sm text-slate-600"><X className="mt-0.5 size-4 shrink-0 text-red-500" />{t(`landing.accountDeletion.deletedItem${key}`)}</li>);
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <ul className="mt-3 flex flex-col gap-2">{t7}</ul>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t) {
    t9 = t("landing.accountDeletion.retainedNote");
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <p className="mt-4 text-sm text-slate-500">{t9}</p>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t6 || $[22] !== t8) {
    t11 = <section className="rounded-lg border border-slate-200 bg-slate-50 p-5">{t6}{t8}{t10}</section>;
    $[20] = t10;
    $[21] = t6;
    $[22] = t8;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = form.handleSubmit;
  const t13 = Boolean(form.touched.phone && form.errors.phone);
  let t14;
  if ($[24] !== t) {
    t14 = t("landing.accountDeletion.phoneLabel");
    $[24] = t;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t14) {
    t15 = <FieldLabel htmlFor="deletion-phone">{t14}</FieldLabel>;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const t16 = form.values.phone;
  const t17 = Boolean(form.touched.phone && form.errors.phone);
  let t18;
  if ($[28] !== form) {
    t18 = formatted => {
      form.setStatus(undefined);
      form.setFieldValue("phone", formatted);
    };
    $[28] = form;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== form.handleBlur || $[31] !== form.values.phone || $[32] !== t17 || $[33] !== t18) {
    t19 = <PhoneInput id="deletion-phone" name="phone" value={t16} aria-invalid={t17} onBlur={form.handleBlur} onValueChange={t18} />;
    $[30] = form.handleBlur;
    $[31] = form.values.phone;
    $[32] = t17;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== form.errors || $[36] !== form.touched.phone) {
    t20 = form.touched.phone && form.errors.phone ? <FieldError errors={[{
      message: form.errors.phone
    }]} /> : null;
    $[35] = form.errors;
    $[36] = form.touched.phone;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t13 || $[39] !== t15 || $[40] !== t19 || $[41] !== t20) {
    t21 = <Field className="gap-1.5" data-invalid={t13}>{t15}{t19}{t20}</Field>;
    $[38] = t13;
    $[39] = t15;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  const t22 = Boolean(form.touched.confirm && form.errors.confirm);
  const t23 = form.values.confirm;
  const t24 = Boolean(form.touched.confirm && form.errors.confirm);
  let t25;
  if ($[43] !== form) {
    t25 = checked => {
      form.setFieldTouched("confirm", true, false);
      form.setFieldValue("confirm", checked === true);
    };
    $[43] = form;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== form.values.confirm || $[46] !== t24 || $[47] !== t25) {
    t26 = <Checkbox id="deletion-confirm" className="mt-0.5" checked={t23} aria-invalid={t24} onCheckedChange={t25} />;
    $[45] = form.values.confirm;
    $[46] = t24;
    $[47] = t25;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== t) {
    t27 = t("landing.accountDeletion.confirmLabel");
    $[49] = t;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== t27) {
    t28 = <FieldLabel htmlFor="deletion-confirm" className="cursor-pointer text-sm leading-snug font-normal text-slate-600">{t27}</FieldLabel>;
    $[51] = t27;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== t26 || $[54] !== t28) {
    t29 = <div className="flex items-start gap-2.5">{t26}{t28}</div>;
    $[53] = t26;
    $[54] = t28;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== form.errors || $[57] !== form.touched.confirm) {
    t30 = form.touched.confirm && form.errors.confirm ? <FieldError errors={[{
      message: form.errors.confirm
    }]} /> : null;
    $[56] = form.errors;
    $[57] = form.touched.confirm;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== t22 || $[60] !== t29 || $[61] !== t30) {
    t31 = <Field className="gap-1.5" data-invalid={t22}>{t29}{t30}</Field>;
    $[59] = t22;
    $[60] = t29;
    $[61] = t30;
    $[62] = t31;
  } else {
    t31 = $[62];
  }
  let t32;
  if ($[63] !== t21 || $[64] !== t31) {
    t32 = <FieldGroup className="gap-4">{t21}{t31}</FieldGroup>;
    $[63] = t21;
    $[64] = t31;
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  let t33;
  if ($[66] !== t) {
    t33 = t("landing.accountDeletion.graceNote");
    $[66] = t;
    $[67] = t33;
  } else {
    t33 = $[67];
  }
  let t34;
  if ($[68] !== t33) {
    t34 = <p className="text-sm text-slate-500">{t33}</p>;
    $[68] = t33;
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  let t35;
  if ($[70] !== form.isSubmitting) {
    t35 = form.isSubmitting && <Spinner />;
    $[70] = form.isSubmitting;
    $[71] = t35;
  } else {
    t35 = $[71];
  }
  let t36;
  if ($[72] !== form.isSubmitting || $[73] !== t) {
    t36 = form.isSubmitting ? t("landing.accountDeletion.sending") : t("landing.accountDeletion.sendCode");
    $[72] = form.isSubmitting;
    $[73] = t;
    $[74] = t36;
  } else {
    t36 = $[74];
  }
  let t37;
  if ($[75] !== form.isSubmitting || $[76] !== t35 || $[77] !== t36) {
    t37 = <Button className="w-full" type="submit" variant="destructive" disabled={form.isSubmitting}>{t35}{t36}</Button>;
    $[75] = form.isSubmitting;
    $[76] = t35;
    $[77] = t36;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[79] !== form.handleSubmit || $[80] !== t32 || $[81] !== t34 || $[82] !== t37) {
    t38 = <form noValidate={true} onSubmit={t12} className="flex flex-col gap-4">{t32}{t34}{t37}</form>;
    $[79] = form.handleSubmit;
    $[80] = t32;
    $[81] = t34;
    $[82] = t37;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] !== t11 || $[85] !== t38) {
    t39 = <div className="mt-8 flex flex-col gap-8">{t11}{t38}</div>;
    $[84] = t11;
    $[85] = t38;
    $[86] = t39;
  } else {
    t39 = $[86];
  }
  return t39;
}
function _temp2(resolve) {
  return setTimeout(resolve, MOCK_DELAY_MS);
}
function _temp(value) {
  return isValidPhone(value ?? "");
}